import { z, ZodType } from 'zod';

/**
 * Ease usage in IDE by decomposing complex types
 * Decompose combined types into simple object types
 * A: {a: string}
 * B: A & {b: int}
 * Readable<B> => {a: string, b:int}
 */
export type Readable<T> = T extends infer U ? { [K in keyof U]: U[K] } : never;

export type AllKeys<T> = T extends unknown ? keyof T : never;
type _ExclusifyUnion<T, K extends PropertyKey> = T extends unknown
    ? Readable<T & Partial<Record<Exclude<K, keyof T>, never>>>
    : never;
/**
 * Builds an exclusive union type from a union type
 * C = ExclusifyUnion<A|B>
 * toto:C = {a: "tata"} OK
 * tata:C = {b: 123} OK
 * tutu: C = {a: "tata", b: 123} KO => Types of property 'a' are incompatible. Type 'string' is not assignable to type 'undefined'.
 * This is the kind of error shown when you add too many properties with this definition.
 */
export type ExclusifyUnion<T> = _ExclusifyUnion<T, AllKeys<T>>;

export interface BaseMessage {
    msg: string;
    code: number;
}

export type BaseMessages = BaseMessage[];
export type BaseMessagesOne = [BaseMessage];

export interface ApiDefaultWrapper<U = BaseMessages> {
    o: string;
    f: string;
    error: number;
    messages: U;
    errorMsg?: string;
}

export interface ApiWrapper<T, U = BaseMessages> extends ApiDefaultWrapper<U> {
    result: T;
}

export interface PagingResults<T> {
    cnt: number;
    data: Array<T>;
    facets: null | Array<{}>;
    idQuery?: number | null;
    limit: number | null;
    page: number;
}

export type ApiPagingResults<T, U = BaseMessages> = ApiWrapper<PagingResults<T>, U>;

export const jHrefSchema = z.object({
    href: z.string(),
    target: z.string(),
});
export type jHref = z.infer<typeof jHrefSchema>;

export const orderSchema = z.object({
    name: z.string(),
    sort: z.union([z.literal('ASC'), z.literal('DESC')]),
});
export type Order = z.infer<typeof orderSchema>;

export const filterValueSchema = z.union([
    z.boolean(),
    z.string(),
    z.number(),
    z.array(z.string()),
    z.object({
        srcId: z.union([z.number(), z.string()]),
        srcType: z.string(),
    }),
    z.object({
        targetId: z.union([z.number(), z.string()]),
        targetType: z.string(),
    }),
    z.object({
        start: z.string(),
        end: z.string(),
    }),
]);
export type FilterValue = z.infer<typeof filterValueSchema>;

const operatorSchema = z.union([
    z.literal('<'),
    z.literal('>'),
    z.literal('<='),
    z.literal('>='),
    z.literal('!='),
    z.literal('<>'),
    z.literal('like'),
    z.literal('between'),
    z.literal('is'),
    z.literal('is not'),
    z.literal('in'),
    z.literal('link'),
    z.literal('match'),
    z.literal('equal'),
]);

const methodSchema = z.union([
    z.literal('<'),
    z.literal('>'),
    z.literal('<='),
    z.literal('>='),
    z.literal('!='),
    z.literal('<>'),
    z.literal('+'),
    z.literal('-'),
    z.literal('like'),
    z.literal('between'),
    z.literal('is'),
    z.literal('is not'),
    z.literal('in'),
    z.literal('link'),
    z.literal('match'),
    z.literal('equal'),
]);

export const filterSchema = z.object({
    name: z.string(),
    value: filterValueSchema,
    operator: operatorSchema.optional(),
    method: methodSchema.optional(),
    or: z.boolean().optional(),
    isNull: z.boolean().optional(),
    isNotNull: z.boolean().optional(),
});
export type Filter = z.infer<typeof filterSchema>;

export const ordersSchema = orderSchema.array();
export type Orders = z.infer<typeof ordersSchema>;

export const filtersSchema = z.array(filterSchema);
export type Filters = z.infer<typeof filtersSchema>;

export type ListOrder = { name: string; sort: string };

export type ListQueryParameters = {
    limit?: number;
    page?: number;
    filters?: Array<Filter>;
    orders?: Array<ListOrder>;
};

export const inferMaybeWithToken = <T extends ZodType>(type: T) => {
    return type.and(
        z.object({
            token: z.string().or(z.undefined()).optional(),
        }),
    );
};
export type MaybeWithToken<T> = z.infer<ReturnType<typeof inferMaybeWithToken<ZodType<T>>>>;

export enum Format {
    LITTLE = 'raw-little',
    LIST = 'raw-list',
    VIEW = 'raw-view',
    /**
     * @deprecated
     */
    USER_API = 'user-api',
    /**
     * @deprecated
     */
    MOBILE = 'mobile',
}

const formatSchema = z.nativeEnum(Format).transform((v) => `${v}` as const);
export const formatListSchema = z.union([formatSchema, z.undefined()]);
export type FormatList = z.infer<typeof formatListSchema>;

export type ApiReturn<T extends FormatList, Little, List = Little, View = List, Default = {}> = T extends 'raw-little'
    ? Little
    : T extends 'raw-list'
    ? List
    : T extends 'raw-view'
    ? View
    : Default;

export type ValueOf<T> = T[keyof T];

export const objectListPropsSchema = z.object({
    additionalFilter: z.object({}).optional(),
    facets: z.array(z.object({})).optional(),
    filters: filtersSchema.optional(),
    query: z.string().optional(),
    limit: z.number().optional(),
    page: z.number().optional(),
    orders: ordersSchema.optional(),
    format: formatListSchema.optional(),
});
export type ObjectListProps = z.infer<typeof objectListPropsSchema>;
